import {Box, IconButton, Paper, Tooltip} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {ReactNode} from "react";
import {PANEL_HANDLE_WIDTH_PX, PANEL_SLIDE_TRANSITION} from "./useNavLayout";
import {useSlideGesture} from "./useSlideGesture";
import {FLOATING_BUTTON_SX} from "./browserStyles";

// The handle is all that is left on screen once the panel is closed, so it has to stay a comfortable
// target on a phone.
const HANDLE_HEIGHT_PX = 56;

// Shared by the panel and its handle, so the two cast one shadow instead of each casting its own.
const PANEL_ELEVATION = 8;

// Room left around the handle for that shadow to fall into, past the widest blur the elevation uses.
const HANDLE_SHADOW_ROOM_PX = 24;

export interface NavigationSidePanelProps {
    width: number;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    children: ReactNode;
}

/**
 * The landscape navigation: a panel down the right edge that slides out of the way, leaving only its
 * handle behind. The handle sits inside the sliding container, so one transform carries both.
 *
 * It can be swiped shut anywhere on it as well as tapped shut by the handle. Taps are left to the handle
 * itself, which is also how a keyboard reaches it.
 */
export function NavigationSidePanel(props: NavigationSidePanelProps) {
    const {width, isOpen, setIsOpen, children} = props;

    const {offset, isDragging, handlers} = useSlideGesture({
        axis: "x",
        closedOffset: width,
        isOpen,
        setIsOpen,
        // The room list scrolls inside the same container that slides.
        yieldsToCrossAxisScroll: true,
        // Without this a swipe across a room row would also travel to it.
        swallowClickAfterDrag: true
    });

    const label = isOpen ? "Hide navigation" : "Show navigation";

    return <Box {...handlers}
                sx={{
                    pointerEvents: "auto",
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: width,
                    transform: `translateX(${offset}px)`,
                    // While dragging, follow the pointer exactly; animate only when settling.
                    transition: isDragging ? "none" : `transform ${PANEL_SLIDE_TRANSITION}`,
                    // Leaves the browser the vertical panning the room list lives on, and keeps the
                    // sideways gesture it would otherwise spend on going back a page.
                    touchAction: "pan-y"
                }}>

        <Tooltip title={label}>
            <IconButton aria-label={label}
                        aria-expanded={isOpen}
                        onClick={() => setIsOpen(!isOpen)}
                        sx={{
                            ...FLOATING_BUTTON_SX,
                            position: "absolute",
                            // Just outside the panel, so it stays on screen when the panel does not.
                            right: "100%",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: PANEL_HANDLE_WIDTH_PX,
                            height: HANDLE_HEIGHT_PX,
                            // The tab is narrower than the icon button's own padding allows for.
                            padding: 0,
                            borderRadius: "8px 0 0 8px",
                            boxShadow: PANEL_ELEVATION,
                            // Trimmed flush on the panel side, where a shadow would draw the very seam
                            // the tab is trying not to have.
                            clipPath: `inset(-${HANDLE_SHADOW_ROOM_PX}px 0px -${HANDLE_SHADOW_ROOM_PX}px -${HANDLE_SHADOW_ROOM_PX}px)`,
                            // Nothing scrolls here, so the handle can be dragged in any direction.
                            touchAction: "none"
                        }}>
                {isOpen ? <ChevronRightIcon fontSize={"small"}/> : <ChevronLeftIcon fontSize={"small"}/>}
            </IconButton>
        </Tooltip>

        <Paper elevation={PANEL_ELEVATION} square
               sx={{
                   height: "100%",
                   display: "flex",
                   flexDirection: "column",
                   minHeight: 0,
                   overflow: "hidden"
               }}>
            {children}
        </Paper>
    </Box>;
}
